import { useCallback, useEffect, useRef } from 'react';
import type {
  AnalyticsDialogsRequest,
  AnalyticsIntervalData,
  GetLeadsStatsAnalyticsStatsLeadsPostApiArg,
} from '@shared/api';
import { useGetLeadsStatsAnalyticsStatsLeadsPostMutation } from '@shared/api';
import type { AnalyticsStatsResult } from '../lib/types';

export const useLeadsStats = (
  params: AnalyticsDialogsRequest | null,
  enabled = true,
): AnalyticsStatsResult => {
  const [trigger, result] = useGetLeadsStatsAnalyticsStatsLeadsPostMutation();
  const lastArgsRef = useRef<GetLeadsStatsAnalyticsStatsLeadsPostApiArg | null>(null);

  const fetch = useCallback(
    async (p: AnalyticsDialogsRequest) => {
      const arg: GetLeadsStatsAnalyticsStatsLeadsPostApiArg = {
        analyticsDialogsRequest: p,
      };
      lastArgsRef.current = arg;
      try {
        await trigger(arg).unwrap();
      } catch {}
    },
    [trigger],
  );

  const refetch = useCallback(() => {
    if (!lastArgsRef.current) return;
    void trigger(lastArgsRef.current)
      .unwrap()
      .catch(() => {});
  }, [trigger]);

  useEffect(() => {
    if (!enabled || !params) return;
    void fetch(params);
  }, [enabled, params, fetch]);

  const data: AnalyticsIntervalData | undefined = result.data;

  return {
    data,
    intervals: data?.intervals ?? [],
    totalCount: data?.total_count ?? 0,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetch,
    reset: result.reset,
  };
};
